import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import logo from '../assets/logo.webp';
import gem from '../assets/gem.webp';
import chest from '../assets/chest.webp';
import artRef from '../assets/art_ref.webp';
import { DOT_LEFT, trackScale } from '../campaign';
import { Crop, BgRot, GoldButton, fitScene } from './Shared';
import { useLottie, loadChestReveal, prefetchLottie } from './Lottie';

/* ------------------------------------------------------------------
   "How to play" story, Instagram style. An "Introducing" title card
   (2.55s) leads into three auto-advancing slides (8s): the demo track
   ("The Adventure Starts Now"), the Gold Chest — the headline and draw
   copy over a chest that opens and spills the prizes (a Lottie) — and
   Referrals: an endless Gem rail (4 friends → the Gold Chest, then the
   Gems keep coming), then the launch date and the Start CTA. Tap right →
   next, tap left → previous, press & hold → pause, X → skip. The last
   slide waits for the CTA (or a tap) and then calls onEnd. Laid out on
   the 375px stage; the parent sizes/zooms it.
   ------------------------------------------------------------------ */

const INTRO_MS = 2550;
const DUR = 8000;
const LAST = 2;
const OUT_MS = 460;
const CHEST_DELAY_MS = 500; // the chest fades in, then opens
const REF_DELAY_MS = 1300;  // the referral rail starts once the card has risen
const DEMO0 = { step: 0, line: 0, pop: 0, chest: false, flip: 0 };
const EQ_LINES = ['Every ₹500 = 1 Gem', '₹1,000 = 2 Gems', '₹1,500 = 3 Gems', '₹2,000 = 4 Gems + a Gold Chest'];
/* slide 1's content block: top:104 → demo bottom 613 on the design; it may
   rise to 80 (under the header) and must clear the bottom-anchored launch
   line + CTA (812 − 696 + a 16px gap) — see fitScene() in Shared.js */
const SCENE = { designTop: 104, height: 509, minTop: 80, bottom: 132 };
/* slide 2's: the copy at 168 down to the chest's bottom edge at 744 */
const SCENE_CHEST = { designTop: 168, height: 576, minTop: 80, bottom: 68 };
/* slide 3's: the referral art at 142 down to the ∞ note's bottom edge at 652;
   it must clear the launch line + CTA like slide 1 */
const SCENE_REF = { designTop: 142, height: 510, minTop: 80, bottom: 132 };

/* the referral rail: slot i sits at 6 + 54·i — gems 1–4, the chest in slot 5,
   then gems 5… (so gem k ≥ 5 is in slot k + 1); 13 gems per loop */
const RAIL_X = (i) => 6 + i * 54;
const RAIL_GEMS = 13;
const railPos = (k) => (k <= 4 ? RAIL_X(k) : RAIL_X(k + 1));
const RAIL_GEM_KEYS = Array.from({ length: RAIL_GEMS }, (_, i) => i + 1);

/* 12 twinkling specks, randomised once per mount */
function makeBokeh() {
  return Array.from({ length: 12 }, (_, i) => {
    const sz = +(2 + Math.random() * 3).toFixed(1);
    const col = Math.random() < 0.55 ? '#F6D5AC' : (Math.random() < 0.5 ? '#DCC4FF' : '#FBCC65');
    return {
      id: i,
      style: {
        left: `${(Math.random() * 100).toFixed(1)}%`,
        top: `${(8 + Math.random() * 88).toFixed(1)}%`,
        width: sz,
        height: sz,
        background: col,
        boxShadow: `0 0 ${sz * 2}px ${col}`,
        '--d': `${(2.6 + Math.random() * 3.4).toFixed(2)}s`,
        '--dl': `${(-Math.random() * 6).toFixed(2)}s`,
      },
    };
  });
}

/* setTimeout that is cleared on unmount */
function useTimers() {
  const ids = useRef(new Set());
  const set = useCallback((fn, ms) => {
    const id = setTimeout(() => { ids.current.delete(id); fn(); }, ms);
    ids.current.add(id);
    return id;
  }, []);
  const clear = useCallback((id) => { clearTimeout(id); ids.current.delete(id); }, []);
  useEffect(() => {
    const pending = ids.current;
    return () => pending.forEach(clearTimeout);
  }, []);
  return { set, clear };
}

/* Invisible tap target. A quick tap fires onTap; holding for 220ms fires
   onHold and the release fires onRelease instead of onTap. */
function TapZone({ side, onTap, onHold, onRelease }) {
  const held = useRef(false);
  const holdT = useRef(0);
  useEffect(() => () => clearTimeout(holdT.current), []);

  const down = () => {
    held.current = false;
    clearTimeout(holdT.current);
    holdT.current = setTimeout(() => { held.current = true; onHold(); }, 220);
  };
  const up = () => {
    clearTimeout(holdT.current);
    if (held.current) { held.current = false; onRelease(); return; }
    onTap();
  };
  const cancel = () => {
    clearTimeout(holdT.current);
    if (held.current) { held.current = false; onRelease(); }
  };
  return (
    <div
      className={`gq-tap gq-${side}`}
      onPointerDown={down}
      onPointerUp={up}
      onPointerCancel={cancel}
      onPointerLeave={cancel}
    />
  );
}

/* title card — "Introducing" + the logo, with a burst and a light sweep */
function Intro({ stageHeight }) {
  /* centred on the stage the way it sits on the 812px design (eyebrow at 300) */
  const top = Math.round(stageHeight / 2 - 106);
  return (
    <div className="gq-intro gq-play" aria-hidden="true">
      <div className="gq-iscene" style={{ top }}>
        <div className="gq-ieyebrow">Introducing</div>
        <div className="gq-iburst" />
        <Crop kind="logo" className="gq-ilogo" alt="The Golden Quest" />
        <div className="gq-isweep" />
      </div>
    </div>
  );
}

/* slide 1 — the logo, the promise and a live demo of the Gem track */
function SlideStart({ demo, scene }) {
  const step = demo.step;
  return (
    <div className="gq-scene" style={scene}>
      <Crop kind="logo" className="gq-s-logo gq-a-logo" alt="The Golden Quest" />
      <div className="gq-copy gq-top">
        <div className="gq-h gq-a-h">The Adventure Starts Now</div>
        <div className="gq-p gq-a-p">Complete tasks to earn Gems, open Gems and win Bitcoin or Gold prizes.</div>
      </div>
      <div className="gq-demo gq-a-demo">
        <div className="gq-track">
          <div className="gq-fill" style={{ transform: `scaleX(${trackScale(step)})` }} />
          <div className="gq-dot" style={{ transform: `translateX(${DOT_LEFT[step] - 2}px)` }} />
        </div>
        {[1, 2, 3, 4].map((k) => (
          <Crop
            key={k}
            kind="gem"
            className={`gq-gem gq-g${k}${k <= step ? ' gq-on' : ''}${demo.pop === k ? ' gq-pop' : ''}`}
          />
        ))}
        <div className={`gq-chest${demo.chest ? ' gq-earned' : ''}`}>
          <Crop kind="chest" className="gq-cglow" />
          <Crop kind="chest" />
        </div>
        {['₹0', '₹500', '₹1000', '₹1500', '₹2000'].map((label, k) => (
          <div key={label} className={`gq-t gq-lab gq-l${k}`}>{label}</div>
        ))}
        <div className="gq-eq">
          {/* re-keyed so the flip animation replays on every line change */}
          <span key={demo.flip} className={demo.flip ? 'gq-flip' : ''}>{EQ_LINES[demo.line]}</span>
        </div>
      </div>
    </div>
  );
}

/* slide 2 — "Complete a streak to get a Gold Chest", the draw copy, the chest
   that opens and spills the prizes (assets/lottie/chestReveal, played once
   per visit) */
function SlideChest({ drawLabel, scene }) {
  const lotRef = useRef(null);
  const reveal = useLottie(lotRef, { load: loadChestReveal, preserveAspectRatio: 'xMidYMid meet' });
  useEffect(() => {
    reveal.goToAndStop(0);
    const t = setTimeout(() => reveal.goToAndPlay(0), CHEST_DELAY_MS);
    return () => clearTimeout(t);
  }, [reveal]);

  return (
    <div className="gq-scene" style={scene}>
      <div className="gq-copy gq-top">
        <div className="gq-h gq-a-h">Complete a streak to get a Gold Chest</div>
        <div className="gq-p gq-a-p">
          Open your Gold Chests on {drawLabel} for mega prizes like an iPhone Duo, PlayStation 5, Apple Watch, Apple AirPods, Bitcoin, Gold and so much more!
        </div>
      </div>
      <div className="gq-chestlot" ref={lotRef} aria-hidden="true" />
    </div>
  );
}

/* slide 3 — the referral art, the promise and the Referrals rail: the Gem
   track fills one friend at a time, the 4th earns the Gold Chest, then the
   track stretches on and the rail scrolls as the Gems keep coming (the ∞ note
   fades in at the 7th); it loops while the slide is up. The rail is driven
   imperatively — the nodes are static and never re-rendered — then the
   launch date and the CTA, bottom-anchored like the prototype */
function SlideReferral({ startsLabel, ctaLabel, scene, onCta }) {
  const sceneRef = useRef(null);

  useEffect(() => {
    const root = sceneRef.current;
    if (!root) return undefined;
    const q = (sel) => root.querySelector(sel);
    const rail = q('.gq-rs-rail');
    const track = q('.gq-rs-track');
    const fill = q('.gq-rs-fill');
    const dot = q('.gq-rs-dot');
    const cnt = q('.gq-rs-cnt');
    const eq = q('.gq-rs-eq');
    const inf = q('.gq-rs-inf');
    const chestEl = q('.gq-rs-it.gq-ch');
    const badge = q('.gq-rs-badge');
    const moving = [rail, track, fill, dot];

    let alive = true;
    let timer = 0;
    const sleep = (ms) => new Promise((resolve) => { timer = setTimeout(resolve, ms); });
    /* the fill and the dot reach rail position x */
    const reach = (x) => {
      fill.style.transform = `scaleX(${((x + 4) / 2000).toFixed(4)})`;
      dot.style.transform = `translateX(${(x - 6).toFixed(1)}px)`;
    };

    (async () => {
      await sleep(REF_DELAY_MS);
      while (alive) {
        /* reset, with the transitions off */
        moving.forEach((el) => { el.style.transition = 'none'; });
        rail.style.transform = 'none';
        track.style.width = '294px';
        fill.style.transform = 'scaleX(.004)';
        dot.style.transform = 'none';
        inf.classList.remove('gq-show');
        root.querySelectorAll('.gq-rs-it').forEach((el) => el.classList.remove('gq-on'));
        badge.getAnimations().forEach((a) => a.cancel());
        cnt.textContent = '0 Gems';
        eq.textContent = 'Every friend = 1 Gem';
        await sleep(450);
        if (!alive) return;
        moving.forEach((el) => { el.style.transition = ''; });

        for (let k = 1; k <= RAIL_GEMS; k++) {
          if (!alive) return;
          if (k === 5) {
            /* the chest moment: 4 friends in, the chest lights up and shakes */
            reach(RAIL_X(5));
            chestEl.classList.add('gq-on');
            chestEl.animate([
              { transform: 'scale(1)' },
              { transform: 'scale(1.55) rotate(-6deg)', offset: 0.35 },
              { transform: 'scale(1.25) rotate(5deg)', offset: 0.7 },
              { transform: 'scale(1)' },
            ], { duration: 900, easing: 'cubic-bezier(.2,1.4,.4,1)' });
            badge.animate([
              { opacity: 0, transform: 'translateY(6px) scale(.8)' },
              { opacity: 1, transform: 'none' },
            ], { duration: 450, fill: 'forwards', easing: 'cubic-bezier(.2,1.2,.3,1)' });
            eq.textContent = '4 friends = your Gold Chest';
            await sleep(1500);
            if (!alive) return;
            track.style.width = '2000px';
            eq.textContent = 'Only one chest. But the Gems don\u2019t stop…';
            await sleep(900);
            if (!alive) return;
          }
          const x = railPos(k);
          reach(x);
          const g = q(`.gq-rs-it[data-k="${k}"]`);
          g.classList.add('gq-on');
          g.animate([
            { transform: 'scale(1)' },
            { transform: 'scale(1.6)', offset: 0.4 },
            { transform: 'scale(1)' },
          ], { duration: 550, easing: 'cubic-bezier(.2,1.5,.4,1)' });
          cnt.textContent = `${k >= 5 ? 'Chest ✓ · ' : ''}${k} Gem${k > 1 ? 's' : ''}`;
          cnt.animate([{ transform: 'scale(1.25)' }, { transform: 'none' }], { duration: 300 });
          if (k >= 5) rail.style.transform = `translateX(-${Math.max(0, x - 232)}px)`;
          if (k === 7) {
            eq.textContent = '5, 6, 7… every friend is another Gem';
            inf.classList.add('gq-show');
          }
          await sleep(k <= 4 ? 720 : (k <= 7 ? 560 : 420));
        }
        eq.textContent = 'No limit. Refer more, earn more.';
        await sleep(2400);
      }
    })();

    return () => { alive = false; clearTimeout(timer); };
  }, []);

  return (
    <>
      <div className="gq-scene" style={scene} ref={sceneRef}>
        <div className="gq-refart gq-a-logo"><img src={artRef} alt="" /></div>
        <div className="gq-copy gq-top">
          <div className="gq-h gq-a-h">Refer friends and get unlimited Gems!</div>
          <div className="gq-p gq-a-p">Get a Gem when your friend makes a transaction, buys Gold or a Voucher. Only one chest — but no limit on Gems.</div>
        </div>
        <div className="gq-rstreak gq-a-demo" aria-hidden="true">
          <div className="gq-rs-tag">Referrals</div>
          <div className="gq-rs-cnt">0 Gems</div>
          <div className="gq-rs-view">
            <div className="gq-rs-rail">
              <div className="gq-rs-track" />
              <div className="gq-rs-fill" />
              <div className="gq-rs-dot" />
              {RAIL_GEM_KEYS.map((k) => (
                <Fragment key={k}>
                  <div className="gq-rs-it" data-k={k} style={{ left: railPos(k) }}><img src={gem} alt="" /></div>
                  <div className="gq-rs-lb" style={{ left: railPos(k) }}>{k === 1 ? '1 friend' : k}</div>
                </Fragment>
              ))}
              <div className="gq-rs-it gq-ch" style={{ left: RAIL_X(5) }}><img src={chest} alt="" /></div>
              <div className="gq-rs-lb gq-ch" style={{ left: RAIL_X(5) }}>Chest</div>
              <div className="gq-rs-badge" style={{ left: RAIL_X(5) }}>Gold Chest earned ✓</div>
            </div>
          </div>
          <div className="gq-rs-eq">Every friend = 1 Gem</div>
        </div>
        <div className="gq-rs-inf">
          <b>∞</b>
          <span>Earn unlimited Gems — the more friends you refer, the more Gems you get.</span>
        </div>
      </div>
      {startsLabel && <div className="gq-starts">{startsLabel}</div>}
      <GoldButton onClick={onCta}>{ctaLabel}</GoldButton>
    </>
  );
}

export default function Story({
  ctaLabel = 'Start', drawLabel = 'Nov 13th', startsLabel = 'Starts on 18th October',
  zoom = 1, stageHeight = 812, onEnd,
}) {
  const [intro, setIntro] = useState(true);
  const [slide, setSlide] = useState(-1);          // -1 while the title card plays
  const [gen, setGen] = useState(0);               // bumps on every navigation → remounts the slide
  const [dir, setDir] = useState('fwd');
  const [outgoing, setOutgoing] = useState(null);  // { idx, gen } of the slide animating out
  const [paused, setPaused] = useState(false);
  const [demo, setDemo] = useState(DEMO0);
  const bokeh = useMemo(makeBokeh, []);
  const fit = useMemo(() => fitScene(stageHeight, SCENE), [stageHeight]);
  const fitChest = useMemo(() => fitScene(stageHeight, SCENE_CHEST), [stageHeight]);
  const fitRef = useMemo(() => fitScene(stageHeight, SCENE_REF), [stageHeight]);

  /* the chest reveal (1MB+ of frames) is wanted ~10s in: fetch it now */
  useEffect(() => { prefetchLottie(loadChestReveal); }, []);

  const { set, clear } = useTimers();
  const slideRef = useRef(-1);
  const genRef = useRef(0);
  const autoRef = useRef(0);
  const demoRef = useRef(0);
  const clock = useRef({ start: 0, left: 0, holding: false });
  const ended = useRef(false);
  const goSlideRef = useRef(() => {});

  /* auto-advance; the last slide never advances on its own */
  const armTimer = useCallback((ms) => {
    clear(autoRef.current);
    clock.current.left = ms;
    clock.current.start = Date.now();
    if (slideRef.current < LAST) {
      autoRef.current = set(() => goSlideRef.current(slideRef.current + 1), ms);
    }
  }, [set, clear]);

  /* slide 1: fill the demo track one Gem at a time */
  const runDemo = useCallback(() => {
    setDemo(DEMO0);
    let k = 0;
    const step = () => {
      if (slideRef.current !== 0 || k >= 4) return;
      k += 1;
      const kk = k;
      setDemo((d) => ({ step: kk, line: kk - 1, pop: kk, chest: d.chest, flip: d.flip + 1 }));
      set(() => setDemo((d) => (d.pop === kk ? { ...d, pop: 0 } : d)), 600);
      if (kk === 4) {
        set(() => {
          setDemo((d) => ({ ...d, chest: true }));
          set(() => setDemo((d) => ({ ...d, chest: false })), 950);
        }, 400);
      } else {
        demoRef.current = set(step, 950);
      }
    };
    demoRef.current = set(step, 500);
  }, [set]);

  const goSlide = useCallback((n) => {
    clear(autoRef.current);
    clear(demoRef.current);
    clock.current.holding = false;
    setPaused(false);

    const prev = slideRef.current;
    const prevGen = genRef.current;
    genRef.current = prevGen + 1;
    slideRef.current = n;
    setDir(n < prev ? 'back' : 'fwd');
    if (prev >= 0) {
      setOutgoing({ idx: prev, gen: prevGen });
      set(() => setOutgoing((o) => (o && o.gen === prevGen ? null : o)), OUT_MS);
    }
    setGen(genRef.current);
    setSlide(n);

    if (n === 0) { setDemo(DEMO0); demoRef.current = set(runDemo, 1500); }
    armTimer(DUR);
  }, [set, clear, runDemo, armTimer]);
  useEffect(() => { goSlideRef.current = goSlide; }, [goSlide]);

  /* the title card plays first, then slide 1 starts */
  const introRef = useRef(0);
  const startSlides = useCallback(() => {
    clear(introRef.current);
    setIntro(false);
    goSlideRef.current(0);
  }, [clear]);
  useEffect(() => {
    introRef.current = set(startSlides, INTRO_MS);
    return () => clear(introRef.current);
  }, [set, clear, startSlides]);

  const pause = useCallback(() => {
    if (slideRef.current < 0 || slideRef.current >= LAST || clock.current.holding) return;
    clock.current.holding = true;
    setPaused(true);
    clear(autoRef.current);
    clock.current.left -= Date.now() - clock.current.start;
  }, [clear]);

  const resume = useCallback(() => {
    if (!clock.current.holding) return;
    clock.current.holding = false;
    setPaused(false);
    armTimer(Math.max(clock.current.left, 400));
  }, [armTimer]);

  const end = useCallback(() => {
    if (ended.current) return;
    ended.current = true;
    clear(introRef.current);
    clear(autoRef.current);
    clear(demoRef.current);
    onEnd();
  }, [clear, onEnd]);

  /* a tap during the title card skips straight to slide 1 */
  const next = useCallback(() => {
    if (slideRef.current < 0) startSlides();
    else if (slideRef.current < LAST) goSlide(slideRef.current + 1);
    else end();
  }, [startSlides, goSlide, end]);
  const prev = useCallback(() => {
    if (slideRef.current < 0) startSlides();
    else goSlide(slideRef.current > 0 ? slideRef.current - 1 : 0);
  }, [startSlides, goSlide]);

  const renderSlide = (idx) => {
    if (idx === 0) return <SlideStart demo={demo} scene={fit.style} />;
    if (idx === 1) return <SlideChest drawLabel={drawLabel} scene={fitChest.style} />;
    return (
      <SlideReferral
        startsLabel={startsLabel}
        ctaLabel={ctaLabel}
        scene={fitRef.style}
        onCta={end}
      />
    );
  };

  /* the active slide plus, for OUT_MS, the one leaving; kept in design order */
  const layers = slide >= 0 ? [{ idx: slide, gen, state: 'gq-on' }] : [];
  if (outgoing) layers.push({ idx: outgoing.idx, gen: outgoing.gen, state: 'gq-out' });
  layers.sort((a, b) => a.idx - b.idx || a.gen - b.gen);

  return (
    <div className="gq-overlay" onContextMenu={(e) => e.preventDefault()}>
      <div
        className={`gq-story${paused ? ' gq-paused' : ''}`}
        data-dir={dir}
        style={{ zoom, height: stageHeight }}
      >
        <BgRot />
        <div className="gq-sbg">
          <div className="gq-bggem gq-bgg1"><img src={gem} alt="" /></div>
          <div className="gq-bggem gq-bgg2"><img src={gem} alt="" /></div>
          <div className="gq-bggem gq-bgg3"><img src={gem} alt="" /></div>
          <div className="gq-bokeh">
            {bokeh.map((b) => <i key={b.id} style={b.style} />)}
          </div>
          <div className="gq-vignette" />
        </div>

        <div className="gq-bars" key={gen}>
          {[0, 1, 2].map((k) => (
            <i key={k} className={k < slide ? 'gq-done' : (k === slide ? 'gq-cur' : '')} style={{ '--dur': `${DUR}ms` }} />
          ))}
        </div>

        <div className="gq-sh">
          <div className="gq-av"><div className="gq-avc"><img src={logo} alt="" /></div></div>
          <div className="gq-who"><b>GoSats</b><span>The Golden Quest</span></div>
          <button type="button" className="gq-x" onClick={end} aria-label="Close">
            <svg viewBox="0 0 12 12" fill="none"><path d="M1 1l10 10M11 1L1 11" stroke="#fff" strokeWidth="1.7" strokeLinecap="round" /></svg>
          </button>
        </div>

        <TapZone side="l" onTap={prev} onHold={pause} onRelease={resume} />
        <TapZone side="r" onTap={next} onHold={pause} onRelease={resume} />

        {intro && <Intro stageHeight={stageHeight} />}

        {layers.map((l) => (
          <div key={`${l.idx}-${l.gen}`} className={`gq-slide gq-s${l.idx + 1} ${l.state}`}>
            {renderSlide(l.idx)}
          </div>
        ))}
      </div>
    </div>
  );
}
